import { usePostDoctorApi, usePutDoctorApi } from 'api/doctor'
import { Flex } from 'components/shared/flex'
import { InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Doctor, DoctorForm } from 'entities/doctor'
import { useFetchInstitutionsApi } from 'api/institution'

type UserFormViewProps = {
    form: Form<DoctorForm>
    apiError: ApiError
}

export const UserFormView = ({ form, apiError }: UserFormViewProps) => {
    const institutionsApi = useFetchInstitutionsApi()

    useEffect(() => {
        institutionsApi.execute()
    }, [])

    return (
        <>
            {institutionsApi.isSuccess() && (
                <SelectField
                    form={form}
                    attr="institutionId"
                    apiError={apiError}
                    label="施設"
                    required
                    selectItems={institutionsApi.response.results.map((i) => {
                        return { label: i.name, value: i.id! }
                    })}
                    includeBlank
                />
            )}
            <Flex>
                <InputField form={form} attr="familyName" apiError={apiError} label="姓" required placeholder="例）山田" />
                <InputField form={form} attr="givenName" apiError={apiError} label="名" required placeholder="例）太郎" />
            </Flex>
            <Flex>
                <InputField form={form} attr="familyNameKana" apiError={apiError} label="せい" placeholder="例）やまだ" />
                <InputField form={form} attr="givenNameKana" apiError={apiError} label="めい" placeholder="例）たろう" />
            </Flex>
        </>
    )
}

type NewDoctorFormProps = {
    onComplete?: () => void
    institutionId?: number
}

export const NewDoctorForm = (props: NewDoctorFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostDoctorApi()
    const form = useForm<DoctorForm>({ institutionId: props.institutionId })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Doctorの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UserFormView form={form} apiError={api.apiError} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditDoctorFormProps = {
    doctor: Doctor
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditDoctorForm = (props: EditDoctorFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutDoctorApi()
    const form = useForm<DoctorForm>(props.doctor)

    useEffectSkipFirst(() => {
        form.set(props.doctor)
    }, [props.doctor])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Doctorの編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <UserFormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
